// src/components/investigations/InvestigationDetail.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { format } from 'date-fns';
import InvestigationForm from './InvestigationForm';
import { useNotification } from '../../hooks/useNotification';
import NotificationContainer from '../NotificationContainer';
import { getErrorMessage } from '../../utils/error';
import FriendlyAnalysis from '../common/FriendlyAnalysis';

const InvestigationDetail = ({ investigationId, onClose, refreshKey }) => {
  const { t } = useTranslation();
  const { notifications, removeNotification, showSuccess, showError } = useNotification();
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [availableReports, setAvailableReports] = useState([]);
  const [showAddReport, setShowAddReport] = useState(false);
  const [updatingReportId, setUpdatingReportId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    loadInvestigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId, refreshKey]);

  const loadInvestigation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/journalist/investigations/${investigationId}`);
      setInvestigation(response.data.investigation);
      
      // Load available reports for adding
      const reportsRes = await api.get('/api/reports/reports?limit=100');
      setAvailableReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Failed to load investigation:', error);
      setAvailableReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReport = async (reportId) => {
    try {
      await api.post(`/api/journalist/investigations/${investigationId}/reports`, {
        report_id: reportId,
      });
      setShowAddReport(false);
      loadInvestigation();
      onClose && onClose();
      showSuccess(t('journalist.investigations.reportAdded') || 'Report added to investigation');
    } catch (error) {
      console.error('Failed to add report:', error);
      showError(getErrorMessage(error, 'Failed to add report'));
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Client-side guard (backend also enforces this)
      if (newStatus === 'completed' && investigation?.related_reports?.length) {
        const pending = investigation.related_reports.filter(
          (r) => r.status === 'pending' || r.status === 'pending_review'
        );
        if (pending.length > 0) {
          showError(`Cannot complete investigation: ${pending.length} pending report(s) inside it`);
          return;
        }
      }
      await api.patch(`/api/journalist/investigations/${investigationId}`, {
        status: newStatus,
      });
      loadInvestigation();
      onClose && onClose();
      showSuccess(t('journalist.investigations.updated') || 'Investigation updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      showError(getErrorMessage(error, 'Failed to update investigation'));
    }
  };

  const handleReportStatusUpdate = async (reportId, newStatus) => {
    try {
      setUpdatingReportId(reportId);
      const res = await api.patch(`/api/journalist/investigations/${investigationId}/reports/${reportId}/status`, {
        status: newStatus,
      });

      // Update modal state immediately (so user sees the new status without reopening)
      const updated = res.data?.report;
      setSelectedReport((prev) => {
        if (!prev || prev.id !== reportId) return prev;
        return { ...prev, ...(updated || {}), status: updated?.status || newStatus };
      });

      // Keep list view consistent without requiring a refetch
      setInvestigation((prev) => {
        if (!prev) return prev;
        const related = Array.isArray(prev.related_reports) ? prev.related_reports : [];
        const nextRelated = related.map((r) =>
          r.id === reportId ? { ...r, ...(updated || {}), status: updated?.status || newStatus } : r
        );
        return { ...prev, related_reports: nextRelated };
      });

      // Refresh from server in background to pick up any other changes (verified_at, fact_check_result, etc.)
      await loadInvestigation();
      showSuccess('Report status updated');
    } catch (error) {
      console.error('Failed to update report status:', error);
      showError(getErrorMessage(error, 'Failed to update report status'));
    } finally {
      setUpdatingReportId(null);
    }
  };

  const openReport = async (reportId) => {
    try {
      setSelectedReportId(reportId);
      setSelectedReport(null);
      setReportSummary(null);
      setLoadingReport(true);
      const res = await api.get(`/api/journalist/investigations/${investigationId}/reports/${reportId}`);
      setSelectedReport(res.data.report);
    } catch (error) {
      console.error('Failed to load report:', error);
      showError(getErrorMessage(error, 'Failed to load report'));
      setSelectedReportId(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const summarizeSelectedReport = async () => {
    if (!selectedReportId) return;
    try {
      setLoadingSummary(true);
      const res = await api.post(`/api/journalist/investigations/${investigationId}/reports/${selectedReportId}/summarize`);
      setReportSummary(res.data.summary);
    } catch (error) {
      console.error('Failed to summarize report:', error);
      showError(getErrorMessage(error, 'Failed to summarize report'));
    } finally {
      setLoadingSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600 dark:text-shayek-dark-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (!investigation) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-shayek-dark-500">
        <p>{t('journalist.investigations.notFound')}</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
      completed: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
      archived: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300',
      on_hold: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
    };
    return colors[status] || colors.active;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300',
      medium: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
      high: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400',
      urgent: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
    };
    return colors[priority] || colors.medium;
  };

  return (
    <div>
      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-gray-600 dark:text-shayek-dark-600 hover:text-gray-900 dark:hover:text-shayek-dark-900"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('common.back')}
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => setShowEditForm(true)}
            className="px-4 py-2 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors"
          >
            {t('common.edit')}
          </button>
          <select
            value={investigation.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
          >
            <option value="active">{t('journalist.investigations.status.active')}</option>
            <option value="completed">{t('journalist.investigations.status.completed')}</option>
            <option value="on_hold">{t('journalist.investigations.status.on_hold')}</option>
            <option value="archived">{t('journalist.investigations.status.archived')}</option>
          </select>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-shayek-dark-900 mb-2">{investigation.title}</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(investigation.status)}`}>
              {t(`journalist.investigations.status.${investigation.status}`)}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(investigation.priority)}`}>
              {t(`journalist.investigations.priority.${investigation.priority}`)}
            </span>
            <span className="text-sm text-gray-500 dark:text-shayek-dark-500">
              {format(new Date(investigation.created_at), 'MMM d, yyyy')}
            </span>
          </div>
        </div>

        {investigation.description && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.investigations.details.description')}</h3>
            <p className="text-gray-700 dark:text-shayek-dark-700">{investigation.description}</p>
          </div>
        )}

        {investigation.tags && investigation.tags.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{t('journalist.investigations.details.tags')}</h3>
            <div className="flex flex-wrap gap-2">
              {investigation.tags.map((tag, index) => (
                <span key={index} className="px-3 py-1 bg-gray-100 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900">
              {t('journalist.investigations.details.relatedReports')} ({investigation.related_reports?.length || 0})
            </h3>
            <button
              onClick={() => setShowAddReport(true)}
              className="px-4 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors"
            >
              {t('journalist.investigations.addReport')}
            </button>
          </div>

          {investigation.related_reports && investigation.related_reports.length > 0 ? (
            <div className="space-y-3">
              {investigation.related_reports.map((report) => (
                <div key={report.id} className="bg-gray-50 dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-shayek-dark-900 mb-1">
                        {report.input_preview || report.input_data?.substring(0, 100)}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-shayek-dark-600">
                        <span className="capitalize">{report.category}</span>
                        <span className="capitalize">{report.status}</span>
                        <span>{format(new Date(report.created_at), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => openReport(report.id)}
                        className="px-3 py-2 bg-white dark:bg-shayek-dark-100 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-shayek-dark-200 text-gray-900 dark:text-shayek-dark-900"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-shayek-dark-500 text-center py-8">{t('journalist.investigations.noReports')}</p>
          )}
        </div>
      </div>

      {showEditForm && (
        <InvestigationForm
          investigation={investigation}
          onClose={() => {
            setShowEditForm(false);
            loadInvestigation();
          }}
          onSuccess={() => {
            setShowEditForm(false);
            loadInvestigation();
            onClose && onClose();
          }}
        />
      )}

      {showAddReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
            <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-shayek-dark-900">{t('journalist.investigations.addReport')}</h2>
                <button onClick={() => setShowAddReport(false)} className="text-gray-400 dark:text-shayek-dark-400 hover:text-gray-600 dark:hover:text-shayek-dark-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {availableReports.map((report) => (
                  <div
                    key={report.id}
                    onClick={() => handleAddReport(report.id)}
                    className="bg-gray-50 dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-shayek-dark-100 cursor-pointer transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-shayek-dark-900 mb-1">
                      {report.input_preview || report.input_data?.substring(0, 100)}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-shayek-dark-600">
                      <span className="capitalize">{report.category}</span>
                      <span>{format(new Date(report.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Details Modal */}
      {selectedReportId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
            <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-shayek-dark-900">Report Details</h2>
              <button
                onClick={() => {
                  setSelectedReportId(null);
                  setSelectedReport(null);
                  setReportSummary(null);
                }}
                className="text-gray-400 dark:text-shayek-dark-400 hover:text-gray-600 dark:hover:text-shayek-dark-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {loadingReport ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-shayek-dark-600">Loading report…</p>
                </div>
              ) : selectedReport ? (
                <>
                  <div className="bg-gray-50 dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-shayek-dark-900 mb-2">
                          {selectedReport.input_preview || selectedReport.input_data?.substring(0, 200) || '—'}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700 dark:text-shayek-dark-700">
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Type:</span> <span className="font-medium capitalize">{selectedReport.input_type}</span></div>
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Category:</span> <span className="font-medium capitalize">{selectedReport.category || 'n/a'}</span></div>
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Status:</span> <span className="font-medium capitalize">{selectedReport.status}</span></div>
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Created:</span> <span className="font-medium">{format(new Date(selectedReport.created_at), 'MMM d, yyyy')}</span></div>
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Anonymous:</span> <span className="font-medium">{selectedReport.is_anonymous ? 'Yes' : 'No'}</span></div>
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Source URL:</span> <span className="font-medium break-all">{selectedReport.source_url || '—'}</span></div>
                        </div>
                        {selectedReport.reporter_notes && (
                          <div className="mt-3 text-sm">
                            <span className="text-gray-600 dark:text-shayek-dark-600">Reporter notes:</span>
                            <div className="mt-1 text-gray-800 dark:text-shayek-dark-800 whitespace-pre-wrap">{selectedReport.reporter_notes}</div>
                          </div>
                        )}
                        {selectedReport.input_type === 'text' && selectedReport.input_data && (
                          <div className="mt-4">
                            <h3 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">Full text</h3>
                            <div className="bg-white dark:bg-shayek-dark-100 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-3 text-sm text-gray-800 dark:text-shayek-dark-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                              {selectedReport.input_data}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-full sm:w-64">
                        <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">Update Status</label>
                        <select
                          value={selectedReport.status || 'pending'}
                          onChange={(e) => handleReportStatusUpdate(selectedReport.id, e.target.value)}
                          disabled={updatingReportId === selectedReport.id}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
                        >
                          <option value="pending">Pending</option>
                          <option value="pending_review">Pending Review</option>
                          <option value="verified">Verified</option>
                          <option value="flagged">Flagged</option>
                          <option value="rejected">Rejected</option>
                        </select>
                        {updatingReportId === selectedReport.id && (
                          <div className="mt-2 text-sm text-gray-500 dark:text-shayek-dark-500">Updating…</div>
                        )}

                        <div className="mt-4">
                          <button
                            onClick={summarizeSelectedReport}
                            disabled={loadingSummary}
                            className="w-full px-4 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors disabled:opacity-50"
                          >
                            {loadingSummary ? 'Summarizing…' : 'Summarize'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {reportSummary && (
                    <div className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-gray-900 dark:text-shayek-dark-900">Summary</h3>
                      <div className="text-sm text-gray-700 dark:text-shayek-dark-700 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><span className="text-gray-600 dark:text-shayek-dark-600">Type:</span> <span className="font-medium capitalize">{reportSummary.type}</span></div>
                        <div><span className="text-gray-600 dark:text-shayek-dark-600">Status:</span> <span className="font-medium capitalize">{reportSummary.status}</span></div>
                        {reportSummary.word_count && (
                          <div><span className="text-gray-600 dark:text-shayek-dark-600">Word count:</span> <span className="font-medium">{reportSummary.word_count}</span></div>
                        )}
                      </div>
                      {reportSummary.key_points?.length > 0 && (
                        <div>
                          <div className="text-sm font-medium text-gray-800 dark:text-shayek-dark-800 mb-1">Key points</div>
                          <ul className="list-disc list-inside text-sm text-gray-700 dark:text-shayek-dark-700 space-y-1">
                            {reportSummary.key_points.map((p, idx) => <li key={idx}>{p}</li>)}
                          </ul>
                        </div>
                      )}
                      {reportSummary.ai_summary && (
                        <div className="text-sm text-gray-700 dark:text-shayek-dark-700 whitespace-pre-wrap">{reportSummary.ai_summary}</div>
                      )}
                    </div>
                  )}

                  <div className="bg-white dark:bg-shayek-dark-100 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-4">AI & Fact-check Findings</h3>
                    
                    <div className="space-y-6">
                      {selectedReport.result_json && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">AI Analysis</h4>
                          <FriendlyAnalysis data={selectedReport.result_json} />
                        </div>
                      )}
                      
                      {selectedReport.fact_check_result && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Fact Check</h4>
                          <FriendlyAnalysis data={selectedReport.fact_check_result} type="fact_check" />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-shayek-dark-500">Report not available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationDetail;

